import { Panel, PanelFooter, Spinner, Text } from "@aurem/ui";
import { useAuthorize } from "graphql/user";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useChatPanel } from "root/ChatPanelProvider";
import { FindThreadQuery, PermissionEnum } from "types";
import { ChatInput } from "./components/ChatInput";
import { ChatMessages } from "./components/ChatMessages";
import { ChatPanelHeader } from "./components/ChatPanelHeader";
import { ChatPanelInitialisation } from "./components/ChatPanelInitialisation";
import { useThread } from "./hook/use-thread.hook";

export type ThreadMessage =
  FindThreadQuery["findThread"]["messagesList"][number];

const CHAT_PANEL_WIDTH_KEY = "chat_panel_width";
const MIN_WIDTH = 400;
const MAX_WIDTH = window.innerWidth * 0.8;

export const ChatPanel = () => {
  const { pathname } = useLocation();
  const chatCtx = useChatPanel();

  const isChatsPage = pathname.includes("/chats");
  const canManageAI = useAuthorize(PermissionEnum.ManageAiAssistant);
  const shouldShowPanel = chatCtx.isOpen && !isChatsPage && canManageAI;

  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = localStorage.getItem(CHAT_PANEL_WIDTH_KEY);
    return stored ? parseInt(stored, 10) : MIN_WIDTH;
  });

  const handleResize = useCallback((width: number) => {
    setPanelWidth(width);
    localStorage.setItem(CHAT_PANEL_WIDTH_KEY, String(width));
  }, []);

  const thread = useThread({
    threadId: chatCtx.currentThreadId,
    onThreadIdChange: chatCtx.syncThreadId,
  });

  const isNewConversation =
    !chatCtx.currentThreadId ||
    (thread.messages.length === 0 && !thread.isThinking);

  // Handle pending messages from context
  useEffect(() => {
    if (!chatCtx.pendingMessage || thread.isThinking) return;
    void thread.handleSend(chatCtx.pendingMessage, thread.selectedMode);
    chatCtx.clearPendingMessage();
  }, [
    chatCtx.pendingMessage,
    thread.isThinking,
    thread.handleSend,
    thread.selectedMode,
    chatCtx,
  ]);

  return (
    <Panel
      open={shouldShowPanel}
      onOpenChange={chatCtx.setIsOpen}
      side="right"
      resizable
      width={panelWidth}
      defaultWidth={MIN_WIDTH}
      minWidth={MIN_WIDTH}
      maxWidth={MAX_WIDTH}
      onWidthChange={handleResize}
      className="top-[64px] h-[calc(100vh-64px)] z-[60]"
    >
      <ChatPanelHeader />
      <div ref={thread.containerRef} className="flex-1 min-h-0 overflow-auto">
        {thread.isLoadingThread && thread.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        ) : thread.threadError ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center">
              <Text size="sm" className="text-danger-500">
                {thread.threadError}
              </Text>
            </div>
          </div>
        ) : isNewConversation &&
          thread.messages.length === 0 &&
          !thread.isThinking ? (
          <ChatPanelInitialisation
            onSend={thread.handleSend}
            selectedMode={thread.selectedMode}
          />
        ) : (
          <ChatMessages
            messages={thread.messages}
            streamingState={thread.streamingState}
            messagesEndRef={thread.messagesEndRef}
            onRetryMessage={thread.handleRetry}
            isThinking={thread.isThinking}
            isLoadingMore={thread.isLoadingMore}
            variant="panel"
          />
        )}
      </div>
      <PanelFooter className="p-4">
        <ChatInput
          draft={thread.draft}
          setDraft={thread.setDraft}
          selectedMode={thread.selectedMode}
          setSelectedMode={thread.setSelectedMode}
          isThinking={thread.isThinking}
          onSend={thread.handleSend}
          onStop={thread.handleStop}
          disabled={
            thread.isLoadingThread ||
            !!thread.threadError ||
            !thread.draft.trim()
          }
        />
      </PanelFooter>
    </Panel>
  );
};
